import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { OrderWindow } from "@models/OrderWindow";
// Assuming Program model might be like this, or we can use string[] for IDs directly
// import { Program } from "@models/Program";
import client, { RequestParams, useMutateError } from "@services/api";
import { QueryOptions } from "@utils/reactQuery";

export const orderWindowsKeyFactory = createQueryKeys("order-windows", {
  detail: (id: string) => ({
    queryKey: [id],
    queryFn: () =>
      client.get<OrderWindow>(`order-windows/${id}`).then((res) => res.data),
  }),
  list: (params?: RequestParams) => ({
    queryKey: [params || {}], // Ensure params is always an object for consistent query key
    queryFn: () =>
      client
        .get<OrderWindow[]>("order-windows", { params })
        .then((res) => res.data),
  }),
});

export const useOrderWindowQuery = (id: string | null | undefined) => {
  return useQuery({
    ...orderWindowsKeyFactory.detail(id!),
    enabled: !!id,
    staleTime: 60_000,
  });
};

export const useOrderWindowsListQuery = (
  params?: RequestParams,
  opts?: QueryOptions<OrderWindow[]>
) => {
  return useQuery({
    ...orderWindowsKeyFactory.list({ skipPagination: true, ...params }),
    staleTime: 60_000,
    ...opts,
  });
};

// Payload Types for Mutations
export interface OrderWindowFormData {
  name: string;
  openDate: string; // ISO date string
  closeDate: string; // ISO date string
  reviewCloseDate: string; // ISO date string
  inMarketDate: string; // ISO date string
  channelId?: string | null;
  programIds: string[];
}

export const useCreateOrderWindowMutation = () => {
  const setError = useMutateError();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ programIds, ...payload }: OrderWindowFormData) =>
      client
        .post<OrderWindow>("order-windows", {
          __type: "order-window",
          ...payload,
          programsIds: programIds,
        })
        .then((res) => res.data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: orderWindowsKeyFactory.list._def,
      });
      queryClient.setQueryData(
        orderWindowsKeyFactory.detail(data.id).queryKey,
        data
      );
    },
    onError: (error) => {
      setError(error, "Create Order Window");
    },
  });
};

export const useUpdateOrderWindowMutation = () => {
  const setError = useMutateError();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload: { programIds, ...payload },
    }: {
      id: string;
      payload: Partial<OrderWindowFormData>;
    }) =>
      client
        .put<OrderWindow>(`order-windows/${id}`, {
          __type: "order-window",
          ...payload,
          programsIds: programIds,
        })
        .then((res) => res.data),
    onSuccess: (data, { id }) => {
      queryClient.invalidateQueries({
        queryKey: orderWindowsKeyFactory.list._def,
      });
      queryClient.setQueryData(
        orderWindowsKeyFactory.detail(id).queryKey,
        data
      );
    },
    onError: (error) => {
      setError(error, "Update Order Window");
    },
  });
};
